import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/images/logo1.jpg';
import { MenuItemsData } from '../routes/NavItems';

/**
 * NavBar will listen the scroll event of the main element(querying by id).
 * @returns 
 */
const Navbar = () => {
  const [activeItem, setActiveItem] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const menuItemRefs = useRef([]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setShowDropdown(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (navRef.current && !navRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleClick = () => {
    setShowDropdown(false);
  };

  const handleLogoClick = () => {
    setActiveItem(null);
    setMenuOpen(false);
    setShowDropdown(false);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    const mainDom = document.getElementById('main')

    const handleScroll = (e) => {
      const scrollPosition = mainDom.scrollTop;
      setIsScrolled(scrollPosition > 0);
    };

    mainDom.addEventListener('scroll', handleScroll);
    return () => mainDom.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        showDropdown || isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
      onMouseEnter={() => !isMobile && setShowDropdown(true)}
      onMouseLeave={() => !isMobile && setShowDropdown(false)}
    >
      <div className="max-full mx-auto px-6 sm:px-8 md:px-10 lg:px-20">
        <div className="flex justify-between">
          {/* Logo */}
          <div className="flex items-center py-3" onClick={handleLogoClick}>
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Duralyze Hydrogen Logo" className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto mr-2" />
            </Link>
          </div>

          {/* Primary Nav */}
          <div aria-haspopup="true" className="hidden md:flex items-center">
            {MenuItemsData.map((item, index) => (
              <div
                key={item.id}
                ref={el => menuItemRefs.current[index] = el}
                className="relative w-[150px] text-center"
              >
                <div
                  className={`py-4 inline-flex items-center text-center text-sm md:text-base lg:text-lg hover:text-theme-blue font-bold transition duration-300
                    ${activeItem === item.id ? 'text-theme-blue' : 'text-gray-700'}`}
                  onClick={() => {
                    setActiveItem(item.id);
                    handleClick();
                  }}
                >
                  {item.title}
                  <div className={`absolute bottom-0 left-0 w-full h-1 text-theme-blue transform transition-transform duration-300 z-50
                    ${activeItem === item.id ? 'scale-x-100' : 'scale-x-0'}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button onClick={toggleMenu} className="py-2">
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
          </div>

          {/* Full-width dropdown */}
          {!isMobile && showDropdown && (
            <div className="absolute left-0 right-0 top-full bg-white shadow-lg px-6 sm:px-8 md:px-10 lg:px-20">
              <div className="w-full flex justify-end">
                <div className="flex flex-row">
                  {MenuItemsData.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col items-center w-[150px] text-center px-1"
                    >
                      {item.subItems.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.path}
                          className="text-gray-600 h-12 hover:text-theme-blue whitespace-normal break-words text-xs md:text-sm text-center transition-colors duration-200"
                          onClick={handleClick}
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mobile menu */}
          {menuOpen && (
            <div
              ref={menuRef}
              className="absolute top-0 right-0 w-[300px] bg-white shadow-md h-screen z-40"
            >
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-theme-blue"
                onClick={() => setMenuOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="flex flex-col p-6 space-y-4">
                <Link
                  to="/"
                  className="text-gray-700 text-base font-bold hover:text-theme-blue"
                  onClick={() => setMenuOpen(false)}
                >
                  Home
                </Link>
                <hr />
                {MenuItemsData.map((item) => (
                  <div key={item.id} className="space-y-2">
                    <Link
                      to={item.path}
                      className="text-gray-700 text-base font-bold hover:text-theme-blue"
                      onClick={() => setMenuOpen(false)}
                    >
                      <span>
                        <span className="text-theme-blue">{item.title.charAt(0)}</span>
                        <span className="text-gray-700">{item.title.slice(1)}</span>
                      </span>
                    </Link>
                    <hr />
                    {item.subItems && (
                      <div className="flex flex-col ml-4 space-y-1">
                        {item.subItems.map((subItem, index) => (
                          <Link
                            key={index}
                            to={subItem.path}
                            className="text-gray-700 text-sm hover:text-theme-blue"
                            onClick={() => setMenuOpen(false)}
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;